import { z } from "zod";

import {
  Arch,
  CaninesRatioHowTo,
  CaninesRatioLeft,
  CaninesRatioRight,
  CloseAllGaps,
  ElasticRecoilLeft,
  ElasticRecoilRight,
  ElasticTooth,
  IncisorsLowerHorizontalOverlap,
  IncisorsUpperHorizontalOverlap,
  IncisorsVerticalOverlap,
  LowerArch,
  Midline,
  MolarsRatioHowTo,
  MolarsRatioLeft,
  MolarsRatioRight,
  OcclusalPlane,
  OcclusalPlaneAction,
  Overjet,
  TeethLevelAlign,
  UpperArch,
} from "~/common/prescription";

function enumSchema<T extends Record<string, number>>(values: T) {
  const literals = Object.values(values).map((value) => z.literal(value));
  return z.union(literals as [z.ZodLiteral<number>, z.ZodLiteral<number>, ...z.ZodLiteral<number>[]]);
}

const toothList = z.array(z.number().int());

export const LlmPrescriptionSchema = z.object({
  arch: enumSchema(Arch).nullable(),
  upper_arch_action: enumSchema(UpperArch).nullable(),
  upper_arch_expansion_bodily: z.boolean(),
  upper_arch_expansion_torque: z.boolean(),
  lower_arch_action: enumSchema(LowerArch).nullable(),
  lower_arch_expansion_bodily: z.boolean(),
  lower_arch_expansion_torque: z.boolean(),
  teeth_level_align: enumSchema(TeethLevelAlign).nullable(),
  teeth_level_align_lower: enumSchema(TeethLevelAlign).nullable(),
  occlusal_plane: enumSchema(OcclusalPlane).nullable(),
  occlusal_plane_action: enumSchema(OcclusalPlaneAction).nullable(),
  occlusal_plane_teeth: toothList,
  vertical_overlap: enumSchema(IncisorsVerticalOverlap).nullable(),
  vertical_overlap_comment: z.string(),
  upper_horizontal_overlap: enumSchema(IncisorsUpperHorizontalOverlap).nullable(),
  lower_horizontal_overlap: enumSchema(IncisorsLowerHorizontalOverlap).nullable(),
  overjet: enumSchema(Overjet).nullable(),
  displacement_lower: z.boolean().nullable(),
  midline: enumSchema(Midline).nullable(),
  midline_improve_by: z.string(),
  canine_ratio_right: enumSchema(CaninesRatioRight).nullable(),
  canine_ratio_left: enumSchema(CaninesRatioLeft).nullable(),
  canine_method: enumSchema(CaninesRatioHowTo).nullable(),
  canine_ipr: z.boolean(),
  canine_method_ai: z.boolean(),
  molars_ratio_right: enumSchema(MolarsRatioRight).nullable(),
  molars_ratio_left: enumSchema(MolarsRatioLeft).nullable(),
  molars_method: enumSchema(MolarsRatioHowTo).nullable(),
  molars_ipr: z.boolean(),
  molars_method_ai: z.boolean(),
  close_all_gaps: enumSchema(CloseAllGaps).nullable(),
  close_all_gaps_value: z.string(),
  close_all_gaps_spaces: z.record(z.string(), z.number()),
  elastics_recoil_right: enumSchema(ElasticRecoilRight).nullable(),
  elastics_recoil_left: enumSchema(ElasticRecoilLeft).nullable(),
  elastics_options: z.record(z.string(), enumSchema(ElasticTooth)),
  dont_move: toothList,
  avoid_locks: toothList,
  extractions: toothList,
  teething: toothList,
  comment: z.string(),
});

export const PrescriptionMetaSchema = z.object({
  ready_for_modeling: z.boolean(),
  filled_count: z.number(),
  total_fields: z.number(),
  completion_percent: z.number(),
  blocking_questions: z.array(z.string()),
  recommended_to_clarify: z.array(z.string()),
  assumptions_made: z.array(z.string()),
  low_confidence_fields: z.array(z.string()),
});

export const PrescriptionGenerateStatusSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("processing") }),
  z.object({
    status: z.literal("ready"),
    prescription: LlmPrescriptionSchema.nullable(),
    meta: PrescriptionMetaSchema.nullable(),
    error: z.string().nullable(),
  }),
  z.object({ status: z.literal("failed"), error: z.string() }),
]);

export type LlmPrescription = z.infer<typeof LlmPrescriptionSchema>;
export type PrescriptionGenerateStatus = z.infer<typeof PrescriptionGenerateStatusSchema>;
